import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpItemService from "@plannel/services/fp/fp-item-service";
import ItemPopup from "@plannel/pages/factory-plan/common/popup/ItemPopup";
import StagePopup from "@plannel/pages/factory-plan/common/popup/StagePopup";
import CodePickerRenderer from "@plannel/pages/factory-plan/common/component/CodePickerRenderer";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { useCommonCodeValues, codeComboCellEditorParams, findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, DownloadButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

// old validRules required 4개(inventoryCd/itemCd/stageCd/plantCd) — Stock.js §REQUIRED_FIELDS 패턴.
const REQUIRED_FIELDS = [
  { field: "inventoryCd", label: "FP_INVENTORY_CD" },
  { field: "itemCd", label: "FP_ITEM_CD" },
  { field: "stageCd", label: "FP_STAGE_CD" },
  { field: "plantCd", label: "FP_PLANT_CD" }
];

// itemClassCd(M/I/P)에 따라 itemTpCd가 참조하는 공통코드 그룹이 달라짐(old itemTypeStyleCallBack 대응).
const ITEM_TYPE_GROUP_BY_CLASS = { M: "FP_ITEM_TYPE_M", I: "FP_ITEM_TYPE_I", P: "FP_ITEM_TYPE_P" };
// 품목 선택 시 itemClassCd별 itemTpCd 기본값(old ItemPopup.jsx의 confirm 매핑 그대로).
const DEFAULT_ITEM_TP_CD_BY_CLASS = { M: "M", I: "X", P: "P" };

/**
 * 인벤토리(Inventory) 탭 — old InventoryTab.jsx 대응. ItemTab과 마찬가지로 §11-1(항상 마운트)로
 * 렌더링되며, planScope prop 변경 시에만 자체 재조회한다(§11-3).
 *
 * props: t, viewName, planScope, searchFields(stageCdNm/itemCdNm/inventoryCdNm)
 */
const InventoryTab = ({ t, viewName, planScope, searchFields }) => {
  const gridRef = useRef();
  const [inventory, setInventory] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [itemPopup, setItemPopup] = useState({ open: false, node: null });
  const [stagePopup, setStagePopup] = useState({ open: false, node: null });

  const itemTypeMValuesRef = useCommonCodeValues("FP_ITEM_TYPE_M");
  const itemTypeIValuesRef = useCommonCodeValues("FP_ITEM_TYPE_I");
  const itemTypePValuesRef = useCommonCodeValues("FP_ITEM_TYPE_P");
  const timeUomValuesRef = useCommonCodeValues("FP_TIME_UOM");
  const latestReqRef = useRef(0);

  const itemTypeValuesRefByClass = { M: itemTypeMValuesRef, I: itemTypeIValuesRef, P: itemTypePValuesRef };

  const defaultGridMemo = useMemo(() => ({
    ...FpGridSetting({ title: t("FP_INVENTORY"), viewName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, viewName]);

  // planScope는 서버 조회, stageCdNm/itemCdNm/inventoryCdNm은 AG Grid 외부 필터로 즉시 필터(§6-1).
  // rowData는 절대 새 배열로 갈아치우지 않는다(§6-1 — getRowId 없는 상태에서 rowData 참조가 바뀌면
  // grid row model이 재생성되어 미저장 신규 행이 조회조건 변경 시 사라지는 버그가 있었다).
  const doesExternalFilterPass = (node) => {
    if (!node.data?.id) return true;   // 미저장 신규 행은 조회조건과 무관하게 항상 표시(편집 중 초안)
    const { stageCdNm, itemCdNm, inventoryCdNm } = searchFields;
    const hit = (hay, needle) => (hay ?? "").toLowerCase().includes(needle.toLowerCase());
    const r = node.data;
    if (stageCdNm && !(hit(r.stageCd, stageCdNm) || hit(r.stageNm, stageCdNm))) return false;
    if (itemCdNm && !(hit(r.itemCd, itemCdNm) || hit(r.itemNm, itemCdNm))) return false;
    if (inventoryCdNm && !(hit(r.inventoryCd, inventoryCdNm) || hit(r.inventoryNm, inventoryCdNm))) return false;
    return true;
  };

  const isExternalFilterPresent = () => Object.values(searchFields).some(v => !!v);

  // searchFields는 AG Grid가 직접 구독하는 값이 아니라서, 바뀔 때마다 명시적으로 재평가를 트리거.
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [searchFields]);

  const columnDefs = [
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    // old setCodeColumnStyle 대응 — 신규 행(id 없음)만 코드 편집 가능, 저장된 행은 PK성 코드 잠금.
    { headerName: "FP_INVENTORY_CD", field: "inventoryCd", colId: "inventoryCd", cellClass: "stringType", width: 180,
      editable: params => !params.data?.id },
    { headerName: "FP_INVENTORY_NAME", field: "inventoryNm", colId: "inventoryNm", cellClass: "stringType", width: 240 },
    { headerName: "FP_ITEM_CD", field: "itemCd", colId: "itemCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => setItemPopup({ open: true, node }) } },
    { headerName: "FP_ITEM_NM", field: "itemNm", colId: "itemNm", type: ["nonEditableColumn"], width: 240 },
    // itemClassCd는 itemTpCd 콤보 그룹 선택에만 쓰는 숨김 컬럼(old와 동일, visible:false).
    { headerName: "FP_ITEM_CLASS_CD", field: "itemClassCd", colId: "itemClassCd", hide: true, type: ["nonEditableColumn"] },
    { headerName: "FP_ITEM_TP_CD", field: "itemTpCd", colId: "itemTpCd", width: 110, type: ["centerAligned"],
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: (params) => {
        const grpCd = ITEM_TYPE_GROUP_BY_CLASS[params.data?.itemClassCd] ?? ITEM_TYPE_GROUP_BY_CLASS.M;
        return codeComboCellEditorParams(grpCd, itemTypeValuesRefByClass[params.data?.itemClassCd] ?? itemTypeMValuesRef, t);
      } },
    {
      headerName: "FP_STAGE", groupId: "stage", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "FP_STAGE_CD", field: "stageCd", colId: "stageCd", cellClass: "stringType", width: 180,
          editable: false, cellRenderer: CodePickerRenderer,
          cellRendererParams: { onOpenPopup: node => setStagePopup({ open: true, node }) } },
        { headerName: "FP_STAGE_NM", field: "stageNm", colId: "stageNm", type: ["nonEditableColumn"], width: 240, columnGroupShow: "open" },
        { headerName: "FP_PLANT_CD", field: "plantCd", colId: "plantCd", type: ["nonEditableColumn"], width: 180, columnGroupShow: "open" },
        { headerName: "FP_PLANT_NM", field: "plantNm", colId: "plantNm", type: ["nonEditableColumn"], width: 240, columnGroupShow: "open" }
      ]
    },
    {
      headerName: "FP_STOCK_ATTRIBUTE", groupId: "stockAttribute", marryChildren: true, openByDefault: true,
      children: [
        { headerName: "FP_INV_KEEPING_TM", field: "invKeepingTime", colId: "invKeepingTime", width: 115, type: ["numberColumn", "rightAligned"],
          editable: (params) => params.data?.itemTpCd === "E" },
        { headerName: "FP_INV_FENCE_TM", field: "invFenceTime", colId: "invFenceTime", width: 115, type: ["numberColumn", "rightAligned"],
          editable: (params) => ["S", "R"].includes(params.data?.itemTpCd) },
        { headerName: "FP_TIME_UOM", field: "timeUom", colId: "timeUom", width: 115, type: ["centerAligned"],
          cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
          cellEditorParams: () => codeComboCellEditorParams("FP_TIME_UOM", timeUomValuesRef, t) }
      ]
    },
    { headerName: "FP_STOCK_SPLIT_COMBINATION", field: "stockSplitCombination", colId: "stockSplitCombination", cellClass: "stringType", width: 110 },
    {
      headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
        { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" },
        { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp", columnGroupShow: "open" },
        { headerName: "createdBy", field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" }
      ]
    }
  ];

  const retrieveInventory = () => {
    if (!planScope) return;
    const reqId = ++latestReqRef.current;
    gridRef.current?.api?.setGridOption("loading", true);

    fpItemService.getInventories({ planScope })
      .then(response => {
        if (reqId !== latestReqRef.current) return;
        const results = response.data;
        if (isEmpty(results)) {
          setInventory([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setInventory(results);
        }
      })
      .catch(e => debug(e))
      .then(() => {
        if (reqId !== latestReqRef.current) return;
        DataState.initialize(gridRef.current?.api);
      })
      .finally(() => {
        if (reqId === latestReqRef.current) {
          gridRef.current?.api?.setGridOption("loading", false);
        }
      });
  };

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    retrieveInventory();
  };

  const insertRow = () => {
    const newRow = {
      id: null, planScope, inventoryCd: "", inventoryNm: "", itemCd: "", itemNm: "", itemClassCd: "M",
      itemTpCd: "M", stageCd: "", stageNm: "", plantCd: "", plantNm: "",
      invKeepingTime: 0, invFenceTime: 0, timeUom: "SECOND", stockSplitCombination: ""
    };
    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const removeInventory = () => {
    gridRef.current.api.stopEditing();
    const selectedRows = gridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      fpItemService.removeInventories(selectedRows)
        .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") }))
        .catch(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") }))
        .then(() => retrieveInventory());
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveInventory = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 실제로 비어 있는 필드만 안내한다 — common/grid.js의 findMissingRequired/focusInvalidCell(§9-2).
    const missingRequired = findMissingRequired(gridRef.current.api, REQUIRED_FIELDS);
    if (missingRequired.length > 0) {
      const labels = [...new Set(missingRequired.map(m => t(m.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(gridRef.current.api, missingRequired[0]);
      return;
    }

    fpItemService.upsertInventories(allStateData)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
      .catch(e => { setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") }); debug(e); })
      .then(() => retrieveInventory());
  };

  const onSelectItem = (row) => {
    const node = itemPopup.node;
    if (node) {
      node.setDataValue("itemCd", row.itemCd);
      node.setDataValue("itemNm", row.itemNm);
      node.setDataValue("itemClassCd", row.itemClassCd);
      node.setDataValue("itemTpCd", DEFAULT_ITEM_TP_CD_BY_CLASS[row.itemClassCd] ?? "M");
    }
  };

  const onSelectStage = (row) => {
    const node = stagePopup.node;
    if (node) {
      node.setDataValue("stageCd", row.stageCd);
      node.setDataValue("stageNm", row.stageNm);
      node.setDataValue("plantCd", row.plantCd);
      node.setDataValue("plantNm", row.plantNm);
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") saveInventory();
    else if (action === "DELETE") removeInventory();
  };

  // 화면 내부 탭(§11-3): planScope가 바뀔 때만 재조회.
  useEffect(() => {
    if (!gridRef.current?.api) return;
    retrieveInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <DownloadButton onClick={() => gridRef.current.api.exportDataAsExcel({ fileName: `${t("FP_INVENTORY")}.xlsx`, sheetName: t("FP_INVENTORY") })} />
        <Box sx={{ display: "flex" }}>
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
        </Box>
      </Box>
      <Box id="fp-inventory-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={inventory}
          {...defaultGridMemo}
          rowSelection={{
            ...defaultGridMemo.rowSelection,
            headerCheckbox: true,
            checkboxes: true
          }}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onGridReady={onGridReady}
        />
      </Box>
      <ItemPopup
        open={itemPopup.open}
        onClose={() => setItemPopup({ open: false, node: null })}
        planScope={planScope}
        onSelect={onSelectItem}
      />
      <StagePopup
        open={stagePopup.open}
        onClose={() => setStagePopup({ open: false, node: null })}
        planScope={planScope}
        onSelect={onSelectStage}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(InventoryTab);
